import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { mockCustomers, mockCustomerProfiles } from "../mock";
import { Customer, CustomerProfile } from "../types";
import { Search, Plus, Trash2, Save, User } from "lucide-react";

const CustomerProfiling: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerProfile | null>(null);
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<CustomerProfile | null>(null);

  const handleSearch = (): void => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const results = mockCustomers.filter(
      (customer) =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.accountNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.meterId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone.includes(searchTerm)
    );

    setSearchResults(results as Customer[]);
  };

  const loadCustomerProfile = (customerId: string): void => {
    const existingProfile = mockCustomerProfiles.find(
      (p) => p.id === customerId
    );
    const customer = mockCustomers.find((c) => c.id === customerId);

    if (existingProfile) {
      // Convert mock data to match interface
      const convertedProfile: CustomerProfile = {
        ...existingProfile,
        profileData: {
          ...existingProfile.profileData,
          paymentHistory: existingProfile.profileData.paymentHistory,
          specialInstructions: existingProfile.profileData.specialInstructions,
          monthlyConsumption: {
            average: 0,
            trend: "stable",
          },
          billingHistory: [],
          preferences: {
            communicationChannel: "email",
            paymentMethod: "online",
            notificationSettings: {
              sms: true,
              email: true,
              whatsapp: false,
            },
          },
        },
        customFields: existingProfile.customFields || [],
      };
      setSelectedCustomer(convertedProfile);
      setProfileData(convertedProfile);
    } else if (customer) {
      // Create new profile from basic customer data
      const newProfile: CustomerProfile = {
        ...customer,
        alternatePhone: "",
        email: "",
        profileData: {
          familySize: 0,
          occupationType: "",
          propertyType: "residential",
          connectionType: "single_phase",
          loadCapacity: "",
          installationDate: "",
          lastMaintenanceDate: "",
          paymentHistory: "",
          specialInstructions: "",
          monthlyConsumption: {
            average: 0,
            trend: "stable",
          },
          billingHistory: [],
          preferences: {
            communicationChannel: "email",
            paymentMethod: "online",
            notificationSettings: {
              sms: true,
              email: true,
              whatsapp: false,
            },
          },
        },
        customFields: [],
      };
      setSelectedCustomer(newProfile);
      setProfileData(newProfile);
    }
  };

  const addCustomField = (): void => {
    if (!profileData) return;

    const newField = {
      id: Date.now().toString(),
      fieldName: "",
      fieldValue: "",
      fieldType: "text",
      options: [],
    };

    setProfileData({
      ...profileData,
      customFields: [...(profileData.customFields || []), newField],
    });
  };

  const updateCustomField = (fieldId: string, updates: Partial<any>): void => {
    if (!profileData) return;

    setProfileData({
      ...profileData,
      customFields: profileData.customFields?.map((field) =>
        field.id === fieldId ? { ...field, ...updates } : field
      ),
    });
  };

  const removeCustomField = (fieldId: string): void => {
    if (!profileData) return;

    setProfileData({
      ...profileData,
      customFields: profileData?.customFields?.filter(
        (field) => field.id !== fieldId
      ),
    });
  };

  const saveProfile = () => {
    if (!profileData) return;

    const updatedProfile = {
      ...profileData,
      updatedAt: new Date().toISOString(),
    };

    console.log("Saving customer profile:", updatedProfile);

    // toast({
    //   title: "Profile Saved",
    //   description: "Customer profile has been successfully updated with all custom fields.",
    // });

    setSelectedCustomer(updatedProfile);
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Customer Profiling & Indexing
          </CardTitle>
          <CardDescription>
            Comprehensive customer data collection and profiling for enhanced
            service delivery
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <Input
              placeholder="Search by Name, Meter ID, Account No., or Phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Search Results */}
      {searchResults.length > 0 && !selectedCustomer && (
        <Card>
          <CardHeader>
            <CardTitle>Search Results ({searchResults.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {searchResults.map((customer) => (
                <div
                  key={customer.id}
                  className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => loadCustomerProfile(customer.id)}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium">{customer.name}</h4>
                      <p className="text-sm text-gray-600">
                        {customer.address}
                      </p>
                      <div className="flex space-x-4 text-xs text-gray-500 mt-1">
                        <span>Account: {customer.accountNo}</span>
                        <span>Meter: {customer.meterId}</span>
                        <span>Phone: {customer.phone}</span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      View Profile
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Profile Details */}
      {selectedCustomer && profileData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Basic Information</CardTitle>
                <div className="flex gap-2">
                  {!isEditing ? (
                    <Button onClick={() => setIsEditing(true)} size="sm">
                      Edit Profile
                    </Button>
                  ) : (
                    <>
                      <Button onClick={saveProfile} size="sm">
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                      <Button
                        onClick={() => {
                          setIsEditing(false);
                          setProfileData(selectedCustomer);
                        }}
                        variant="outline"
                        size="sm">
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={profileData.name}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setProfileData({ ...profileData, name: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input value={profileData.accountNo} disabled />
                </div>
                <div>
                  <Label>Meter ID</Label>
                  <Input value={profileData.meterId} disabled />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={profileData.phone}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setProfileData({ ...profileData, phone: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Alternate Phone</Label>
                  <Input
                    value={profileData.alternatePhone || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        alternatePhone: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={profileData.email || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setProfileData({ ...profileData, email: e.target.value })
                    }
                  />
                </div>
              </div>

              <div>
                <Label>Address</Label>
                <Textarea
                  value={profileData.address}
                  disabled={!isEditing}
                  onChange={(e) =>
                    setProfileData({ ...profileData, address: e.target.value })
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Profile Data */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Family Size</Label>
                  <Input
                    type="number"
                    value={profileData.profileData.familySize || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        profileData: {
                          ...profileData.profileData,
                          familySize: parseInt(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Occupation</Label>
                  <Input
                    value={profileData.profileData.occupationType || ""}
                    disabled={!isEditing}
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        profileData: {
                          ...profileData.profileData,
                          occupationType: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Property Type</Label>
                  <Select
                    value={profileData.profileData.propertyType}
                    disabled={!isEditing}
                    onValueChange={(value) =>
                      setProfileData({
                        ...profileData,
                        profileData: {
                          ...profileData.profileData,
                          propertyType: value,
                        },
                      })
                    }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                      <SelectItem value="industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Connection Type</Label>
                  <Select
                    value={profileData.profileData.connectionType}
                    disabled={!isEditing}
                    onValueChange={(value) =>
                      setProfileData({
                        ...profileData,
                        profileData: {
                          ...profileData.profileData,
                          connectionType: value,
                        },
                      })
                    }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single_phase">Single Phase</SelectItem>
                      <SelectItem value="three_phase">Three Phase</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Load Capacity</Label>
                  <Input
                    value={profileData.profileData.loadCapacity || ""}
                    disabled={!isEditing}
                    placeholder="e.g., 5 KW"
                    onChange={(e) =>
                      setProfileData({
                        ...profileData,
                        profileData: {
                          ...profileData.profileData,
                          loadCapacity: e.target.value,
                        },
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Payment History</Label>
                  <Select
                    value={profileData.profileData.paymentHistory || ""}
                    disabled={!isEditing}
                    onValueChange={(value) =>
                      setProfileData({
                        ...profileData,
                        profileData: {
                          ...profileData.profileData,
                          paymentHistory: value,
                        },
                      })
                    }>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="average">Average</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Special Instructions</Label>
                <Textarea
                  value={profileData.profileData.specialInstructions || ""}
                  disabled={!isEditing}
                  placeholder="Any special instructions for field visits..."
                  onChange={(e) =>
                    setProfileData({
                      ...profileData,
                      profileData: {
                        ...profileData.profileData,
                        specialInstructions: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Custom Fields */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Custom Fields</CardTitle>
                  <CardDescription>
                    Additional data points specific to your utility needs
                  </CardDescription>
                </div>
                {isEditing && (
                  <Button onClick={addCustomField} size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Add Field
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {profileData.customFields?.length &&
              profileData.customFields.length > 0 ? (
                <div className="space-y-4">
                  {profileData.customFields.map((field: any) => (
                    <div key={field.id} className="p-4 border rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div>
                          <Label>Field Name</Label>
                          <Input
                            value={field.fieldName}
                            disabled={!isEditing}
                            onChange={(e) =>
                              updateCustomField(field.id, {
                                fieldName: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div>
                          <Label>Field Type</Label>
                          <Select
                            value={field.fieldType}
                            disabled={!isEditing}
                            onValueChange={(value) =>
                              updateCustomField(field.id, { fieldType: value })
                            }>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Text</SelectItem>
                              <SelectItem value="number">Number</SelectItem>
                              <SelectItem value="date">Date</SelectItem>
                              <SelectItem value="select">Select</SelectItem>
                              <SelectItem value="textarea">Textarea</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Field Value</Label>
                          {field.fieldType === "textarea" ? (
                            <Textarea
                              value={field.fieldValue}
                              disabled={!isEditing}
                              onChange={(e) =>
                                updateCustomField(field.id, {
                                  fieldValue: e.target.value,
                                })
                              }
                            />
                          ) : field.fieldType === "select" ? (
                            <Select
                              value={field.fieldValue}
                              disabled={!isEditing}
                              onValueChange={(value) =>
                                updateCustomField(field.id, {
                                  fieldValue: value,
                                })
                              }>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options?.map((option: string) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type={
                                field.fieldType === "number"
                                  ? "number"
                                  : field.fieldType === "date"
                                  ? "date"
                                  : "text"
                              }
                              value={field.fieldValue}
                              disabled={!isEditing}
                              onChange={(e) =>
                                updateCustomField(field.id, {
                                  fieldValue: e.target.value,
                                })
                              }
                            />
                          )}
                        </div>
                        {isEditing && (
                          <Button
                            onClick={() => removeCustomField(field.id)}
                            variant="destructive"
                            size="sm">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <p>No custom fields added yet</p>
                  {isEditing && (
                    <Button onClick={addCustomField} className="mt-2">
                      <Plus className="h-4 w-4 mr-1" />
                      Add First Custom Field
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <Card className="lg:col-span-2">
            <CardContent className="pt-6">
              <div className="flex space-x-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setProfileData(null);
                    setIsEditing(false);
                    setSearchResults([]);
                    setSearchTerm("");
                  }}>
                  Back to Search
                </Button>
                <Button
                  onClick={() => {
                    // toast({
                    //   title: "Work Order Created",
                    //   description:
                    //     "Customer profiling work order has been created and assigned to field agent.",
                    // });
                  }}>
                  Create Profiling Work Order
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CustomerProfiling;
